'use client'

import { Form, FormGroupLabel, FormStore, useFormContext, useFormStore } from '@ariakit/react'
import { ComponentProps, createContext, useRef } from 'react'
import styled, { css } from 'styled-components'

import {
  ColorSwatchWrapper,
  SizeSelectorSelectorButton,
  TypeStyle,
  focusOutline,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import {
  VariantSelectionFormColorOptionFragment,
  VariantSelectionFormFitOptionFragment,
  VariantSelectionFormGroupFieldsFragment,
  VariantSelectionFormSizeOptionFragment,
  VariantSelectionFormVariantFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import { MakeCartItemProps } from '../../../brunswick/containers/cart/utils'
import { usePagesDirectoryStableQueryParams } from '../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { useTranslation } from '../../_providers/TranslationProvider.client'

export const variantSelectionFormColorOption = gql(`
  fragment VariantSelectionFormColorOption on ProductCatalogProductColorOption {
    color {
      id
      hexCodes
      displayValue
      swatchImage {
        url
      }
      group {
        id
        displayValue
      }
    }
    compareAtPriceRange {
      min {
        amount
      }
      max {
        amount
      }
      minPercentageOff
      maxPercentageOff
    }
    priceRange {
      currencyCode
      min {
        amount
      }
      max {
        amount
      }
    }
    outOfStock
  }
`)

export const variantSelectionFormFitOption = gql(`
  fragment VariantSelectionFormFitOption on ProductCatalogProductFitOption {
    fit {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormSizeOption = gql(`
  fragment VariantSelectionFormSizeOption on ProductCatalogProductSizeOption {
    size {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormVariantFields = gql(`
  fragment VariantSelectionFormVariantFields on ProductCatalogVariant {
    __typename
    id
    sku
    availability
    shopifyId
    shopifyProductId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

export const variantSelectionFormGroupFields = gql(`
  fragment VariantSelectionFormGroupFields on ProductCatalogVariantGroup {
    __typename
    id
    title
    availability
    siteWidePromoEligible
    finalSale
    primaryCategory {
      key
      analyticsId
    }
    color {
      id
      hexCodes
      displayValue
      group {
        id
        displayValue
      }
    }
    variant(fit: $fitFieldValue, size: $sizeFieldValue) {
      ...VariantSelectionFormVariantFields
    }
  }
`)

export const variantSelectionFormProductFields = gql(`
  fragment VariantSelectionFormProductFields on ProductCatalogStandardProduct {
    __typename
    id
    handle
    options(color: $colorFieldValue, fit: $fitFieldValue, size: $sizeFieldValue, collectionHandle: $collectionHandle) {
      colors {
        ...VariantSelectionFormColorOption
      }
      fits {
        ...VariantSelectionFormFitOption
      }
      sizes {
        ...VariantSelectionFormSizeOption
      }
    }
  }
`)

export type VariantSelectionFormContextValue = {
  product: {
    handle: string
    options: {
      colors?: VariantSelectionFormColorOptionFragment[]
      fits?: VariantSelectionFormFitOptionFragment[]
      sizes?: VariantSelectionFormSizeOptionFragment[]
    }
  }
  variantGroup: MakeCartItemProps['variantGroup'] & {
    title: string
    color: VariantSelectionFormGroupFieldsFragment['color']
    availability: VariantSelectionFormGroupFieldsFragment['availability']
    primaryCategory: VariantSelectionFormGroupFieldsFragment['primaryCategory'] &
      MakeCartItemProps['variantGroup']['primaryCategory']
  }
  selectedVariant: VariantSelectionFormSelectedVariant | undefined | null
  loading: boolean
  trackingCategory: string
}

export const VariantSelectionFormContext = createContext<
  VariantSelectionFormContextValue | undefined
>(undefined)

export type VariantSelectionFormState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
}

type UseVariantSelectionFormProps = {
  defaultValues: {
    initialColor: string | undefined | null
    initialFit: string | undefined | null
    initialSize: string | undefined | null
  }
}

export const useVariantSelectionForm = ({ defaultValues }: UseVariantSelectionFormProps) => {
  const translations = useTranslation()

  const formStore = useFormStore<VariantSelectionFormState>({
    defaultValues: {
      color: defaultValues.initialColor,
      fit: defaultValues.initialFit,
      size: defaultValues.initialSize,
    },
  })

  formStore.useValidate(() => {
    const sizeFieldName = 'size' as const
    const value = formStore.getValue(sizeFieldName)
    if (!value || value.length === 0) {
      formStore.setError(sizeFieldName, translations.sizesSection.sizeNotSelectedErrorMessage)
    }
  })

  const colorFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['color']>('color') ??
    defaultValues.initialColor ??
    ''

  const fitFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['fit']>('fit') ?? ''

  const sizeFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['size']>('size') ?? ''

  return {
    formStore,
    queryVariables: {
      colorFieldValue,
      sizeFieldValue,
      fitFieldValue,
    },
  }
}

export const VariantSelectionFormFields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${o => o.theme.spacing(6)};
`

export type VariantSelectionFieldProps = {
  onChange?: (newValue: string) => void
}

type VariantSelectionFormSelectedVariant =
  | VariantSelectionFormVariantFieldsFragment
  | undefined
  | null

type VariantSelectionFormBasicState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
}

type VariantSelectionFormProps<T extends VariantSelectionFormBasicState> = {
  store: FormStore<T>
} & VariantSelectionFormContextValue &
  Omit<ComponentProps<typeof Form>, 'store'>

export const VariantSelectionForm = <T extends VariantSelectionFormBasicState>({
  loading,
  product,
  variantGroup,
  selectedVariant,
  trackingCategory,
  ...props
}: VariantSelectionFormProps<T>) => {
  return (
    <VariantSelectionFormContext.Provider
      value={{
        loading,
        product,
        variantGroup,
        selectedVariant,
        trackingCategory,
      }}
    >
      <Form style={{ display: 'contents' }} resetOnSubmit={false} {...props} />
    </VariantSelectionFormContext.Provider>
  )
}

type VariantSelectionFormQueryParamBindingProps = {
  colorKey: string
  fitKey: string
  sizeKey: string
}

export const VariantSelectionFormPagesDirectoryQueryParamBinding = ({
  colorKey,
  fitKey,
  sizeKey,
}: VariantSelectionFormQueryParamBindingProps) => {
  const { query } = usePagesDirectoryStableQueryParams()
  const formContext = useFormContext()
  const colorQueryRef = useRef(query[colorKey])
  const fitQueryRef = useRef(query[fitKey])
  const sizeQueryRef = useRef(query[sizeKey])

  if (colorQueryRef.current !== query[colorKey]) {
    colorQueryRef.current = query[colorKey]
    formContext!.setValue('color', colorQueryRef.current)
  }

  if (fitQueryRef.current !== query[fitKey]) {
    fitQueryRef.current = query[fitKey]
    formContext!.setValue('fit', fitQueryRef.current)
  }

  if (sizeQueryRef.current !== query[sizeKey]) {
    sizeQueryRef.current = query[sizeKey]
    formContext!.setValue('size', sizeQueryRef.current)
  }

  return null
}

export const VariantSelectionOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
  width: 100%;
`

export const VariantSelectionOption = styled(SizeSelectorSelectorButton)`
  min-width: 30px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const VariantSelectionInvisibleInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;

  &:focus-visible + ${ColorSwatchWrapper}, &:focus-visible + ${VariantSelectionOption} {
    ${focusOutline};
    outline-style: solid;
  }
  margin: 0;
  position: absolute;
`

export const variantSelectionFormGroupLabelTypographyCss = css`
  ${TypeStyle.css.headlineMicro};
  font-weight: 700;
  margin-bottom: ${o => o.theme.spacing(2)};
`

export const VariantSelectionFormGroupLabel = styled(FormGroupLabel)`
  ${variantSelectionFormGroupLabelTypographyCss};
  display: flex;
  justify-self: flex-start;
  gap: ${o => o.theme.spacing(1)};
  text-transform: uppercase;
  flex-wrap: wrap;
`

export const VariantSelectionFieldValueDisplay = styled.span`
  color: ${o => o.theme.color.text.tertiary.on.background};
  ${TypeStyle.css.bodyMicro};
  text-transform: none;
`

export const StyledVariantSelectionFromSoldOutMessage = styled.span`
  color: ${o => o.theme.color.stroke.critical.text.on.subtle};
  white-space: nowrap;
`

export const VariantSelectionFromSoldOutMessage = () => {
  const translations = useTranslation()
  const message = translations.detailsSection.soldOutSectionTitleMessage
  return (
    <StyledVariantSelectionFromSoldOutMessage>{message}</StyledVariantSelectionFromSoldOutMessage>
  )
}
